/**
 * Polyfills pour React
 * Ce fichier exporte des versions sécurisées des hooks React
 */

import React from "react";

// Vérifier si React est correctement chargé
if (!React) {
  console.error("React non trouvé - création d'un objet React de secours");
  window.React = {};
}

// Créer des polyfills pour les hooks et fonctions React courantes
export const createContext = (defaultValue) => {
  if (React.createContext) {
    return React.createContext(defaultValue);
  }

  // Version de secours
  const context = {
    Provider: ({ value, children }) => {
      context._currentValue = value;
      return children;
    },
    Consumer: ({ children }) => children(context._currentValue),
    _currentValue: defaultValue,
    displayName: "",
  };

  return context;
};

export const useContext = (context) => {
  if (React.useContext) {
    return React.useContext(context);
  }

  // Version de secours
  return context._currentValue;
};

export const useState = (initialState) => {
  if (React.useState) {
    return React.useState(initialState);
  }

  // Version de secours (non fonctionnelle mais évite les erreurs)
  const state =
    typeof initialState === "function" ? initialState() : initialState;
  return [state, () => {}];
};

export const useEffect = (effect, deps) => {
  if (React.useEffect) {
    return React.useEffect(effect, deps);
  }

  // Version de secours - exécuter l'effet immédiatement
  try {
    const cleanup = effect();
    // En théorie, on devrait appeler cleanup lors du démontage
  } catch (e) {
    console.error("Erreur dans useEffect:", e);
  }
};

// Exporter d'autres hooks React couramment utilisés
export const useRef = (initialValue) => {
  if (React.useRef) {
    return React.useRef(initialValue);
  }

  // Version de secours
  return { current: initialValue };
};

export const useMemo = (factory, deps) => {
  if (React.useMemo) {
    return React.useMemo(factory, deps);
  }

  // Version de secours - juste exécuter la factory
  try {
    return factory();
  } catch (e) {
    console.error("Erreur dans useMemo:", e);
    return undefined;
  }
};

export const useCallback = (callback, deps) => {
  if (React.useCallback) {
    return React.useCallback(callback, deps);
  }

  // Version de secours - retourner simplement la callback
  return callback;
};

// Exporter React lui-même mais avec nos polyfills
const ReactWithPolyfills = {
  ...React,
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
};

// Appliquer aussi au global React
Object.assign(window.React || {}, ReactWithPolyfills);

export default ReactWithPolyfills;
